import {
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  IconButton,
  ListItemText,
  MenuItem,
  Pagination,
  Select,
  Tab,
  Tabs,
  TextField,
  Tooltip,
} from '@mui/material';
import moment from 'moment';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import useTopPanel from 'hooks/useTopPanel';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';

const EXPORT_TYPES = [
  { key: 'customs', label: 'excel.tabs.customs', icon: 'bi bi-people' },
  { key: 'contracts', label: 'excel.tabs.contracts', icon: 'bi bi-file-text' },
  { key: 'payments', label: 'excel.tabs.payments', icon: 'bi bi-cash-stack' },
  { key: 'debitors', label: 'excel.tabs.debitors', icon: 'bi bi-exclamation-octagon' },
  { key: 'income', label: 'excel.tabs.income', icon: 'bi bi-cash-coin' },
];

const STORAGE_PREFIX = 'excel-columns:';

const loadSavedOrder = (type) => {
  try {
    const raw = localStorage.getItem(STORAGE_PREFIX + type);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const saveOrder = (type, codes) => {
  try {
    localStorage.setItem(STORAGE_PREFIX + type, JSON.stringify(codes));
  } catch {
    /* jim o'tamiz */
  }
};

export default function Excel() {
  const axiosPrivate = useAxiosPrivate();
  const { setComponent } = useTopPanel();
  const { t, i18n } = useTranslation();

  const [activeTab, setActiveTab] = useState(0);
  const [columns, setColumns] = useState([]);
  const [selectedColumns, setSelectedColumns] = useState([]);
  const [columnsLoading, setColumnsLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);

  // Filterlar (har tab uchun alohida, tab almashganda reset)
  const [filters, setFilters] = useState({});

  // Tarix
  const [history, setHistory] = useState({ data: [], total: 0, lastPage: 1 });
  const [historyPage, setHistoryPage] = useState(1);
  const [historyLoading, setHistoryLoading] = useState(false);

  const currentType = EXPORT_TYPES[activeTab].key;

  // Dictionary: obyektlar ro'yxati (filter dropdown uchun)
  const { data: objectsList = [] } = useQuery({
    queryKey: '/dictionary/objects3',
    queryFn: async () => {
      const res = await axiosPrivate.get('/dictionary/objects3');
      return res.data.data || [];
    },
    retry: false,
  });

  // Filtr lug'atlari: bloklar (obyektga qarab), xodimlar, to'lov turlari
  const { data: blocksList = [] } = useQuery({
    queryKey: '/dictionary/blocks2',
    queryFn: async () => {
      const res = await axiosPrivate.get('/dictionary/blocks2');
      return res.data.data || [];
    },
    retry: false,
  });
  const { data: staffsList = [] } = useQuery({
    queryKey: '/dictionary/active-staffs',
    queryFn: async () => {
      const res = await axiosPrivate.get('/dictionary/active-staffs');
      return res.data.data || [];
    },
    retry: false,
  });
  const { data: paymentTypes = [] } = useQuery({
    queryKey: '/integration/payment-types',
    queryFn: async () => {
      const res = await axiosPrivate.get('/integration/payment-types');
      return res.data.data || [];
    },
    retry: false,
  });

  useEffect(() => {
    setComponent(<div className='component-title'>{t('excel.title')}</div>);
  }, [i18n.language]);

  useEffect(() => {
    fetchColumns(currentType);
    setFilters({});
    setHistoryPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentType]);

  const fetchColumns = async (type) => {
    setColumnsLoading(true);
    setColumns([]);
    setSelectedColumns([]);
    try {
      const res = await axiosPrivate.get(`/admin/excel/columns/${type}`);
      const data = res?.data?.data;
      const colsArray = Array.isArray(data) ? data : data?.columns ?? [];
      const parsed = colsArray.map((item) => ({
        code: item.key ?? item.code ?? String(item),
        label: item.label ?? item.name ?? item.key ?? String(item),
      }));
      setColumns(parsed);

      const saved = loadSavedOrder(type);
      const allCodes = parsed.map((c) => c.code);
      const restored = Array.isArray(saved)
        ? saved.filter((code) => allCodes.includes(code))
        : [];
      setSelectedColumns(restored);
    } catch {
      setColumns([]);
      setSelectedColumns([]);
    } finally {
      setColumnsLoading(false);
    }
  };

  useEffect(() => {
    if (!columnsLoading && columns.length > 0) {
      saveOrder(currentType, selectedColumns);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedColumns]);

  const fetchHistory = useCallback(
    async (page = 1) => {
      setHistoryLoading(true);
      try {
        const params = new URLSearchParams();
        params.set('type', currentType);
        params.set('page', String(page));
        const res = await axiosPrivate.get(
          `/admin/excel/exports?${params.toString()}`
        );
        const d = res?.data?.data;
        setHistory({
          data: d?.data || [],
          total: d?.total || 0,
          lastPage: d?.last_page || 1,
        });
      } catch {
        setHistory({ data: [], total: 0, lastPage: 1 });
      } finally {
        setHistoryLoading(false);
      }
    },
    [axiosPrivate, currentType]
  );

  useEffect(() => {
    fetchHistory(historyPage);
  }, [fetchHistory, historyPage]);

  const selectedSet = useMemo(
    () => new Set(selectedColumns),
    [selectedColumns]
  );

  const labelByCode = useMemo(() => {
    const m = {};
    columns.forEach((c) => (m[c.code] = c.label));
    return m;
  }, [columns]);

  const availableColumns = useMemo(
    () => columns.filter((c) => !selectedSet.has(c.code)),
    [columns, selectedSet]
  );

  const addColumn = (code) =>
    setSelectedColumns((prev) =>
      prev.includes(code) ? prev : [...prev, code]
    );
  const removeColumn = (code) =>
    setSelectedColumns((prev) => prev.filter((c) => c !== code));
  const move = (idx, dir) => {
    setSelectedColumns((prev) => {
      const next = [...prev];
      const newIdx = idx + dir;
      if (newIdx < 0 || newIdx >= next.length) return prev;
      [next[idx], next[newIdx]] = [next[newIdx], next[idx]];
      return next;
    });
  };
  const selectAll = () => setSelectedColumns(columns.map((c) => c.code));
  const clearAll = () => setSelectedColumns([]);
  const resetOrder = () => setSelectedColumns(columns.map((c) => c.code));

  const updateFilter = (key, value) =>
    setFilters((prev) =>
      // Obyekt o'zgarsa — unga tegishli bo'lmagan blok tanlovi tozalanadi
      key === 'objectId' ? { ...prev, objectId: value, block: '' } : { ...prev, [key]: value }
    );
  const clearFilters = () => setFilters({});

  const activeFilterCount = useMemo(
    () =>
      Object.values(filters).filter(
        (v) => v !== '' && v !== null && v !== undefined && !(Array.isArray(v) && v.length === 0)
      ).length,
    [filters]
  );

  const downloadFromLink = async (link, filename) => {
    if (!link) return;
    const blob = await axiosPrivate.get(link, { responseType: 'blob' });
    const url = URL.createObjectURL(blob.data);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename || 'export.xlsx';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleExport = async () => {
    if (selectedColumns.length === 0) return;
    setExportLoading(true);
    try {
      const params = new URLSearchParams();
      selectedColumns.forEach((col) => params.append('columns[]', col));
      // Filterlarni qo'shish
      Object.entries(filters).forEach(([key, value]) => {
        if (value === '' || value === null || value === undefined) return;
        if (Array.isArray(value)) {
          value.forEach((v) => params.append(`${key}[]`, v));
        } else {
          params.set(key, value);
        }
      });
      const res = await axiosPrivate.get(
        `/admin/excel/${currentType}?${params.toString()}`
      );
      const link = res?.data?.data?.link;
      const filename = res?.data?.data?.filename || 'export.xlsx';
      await downloadFromLink(link, filename);
      setHistoryPage(1);
      fetchHistory(1);
    } finally {
      setExportLoading(false);
    }
  };

  const handleDeleteExport = async (id) => {
    try {
      await axiosPrivate.post(`/admin/excel/delete/${id}`);
      fetchHistory(historyPage);
    } catch {
      /* jim o'tamiz */
    }
  };

  // ---------------------------------------------------------------
  // Filter komponentlari
  // ---------------------------------------------------------------
  const FilterField = ({ children, className = '' }) => (
    <div className={`flex flex-col gap-1 min-w-[160px] ${className}`}>
      {children}
    </div>
  );

  const renderFilters = () => {
    const isvaluteOptions = (
      <Select
        size='small'
        value={filters.isvalute ?? ''}
        displayEmpty
        onChange={(e) => updateFilter('isvalute', e.target.value)}
      >
        <MenuItem value=''>{t('common.filter.all')}</MenuItem>
        <MenuItem value='0'>UZS</MenuItem>
        <MenuItem value='1'>USD</MenuItem>
      </Select>
    );

    const objectSelect = (
      <Select
        size='small'
        value={filters.objectId ?? ''}
        displayEmpty
        onChange={(e) => updateFilter('objectId', e.target.value)}
      >
        <MenuItem value=''>{t('common.filter.all')}</MenuItem>
        {objectsList.map((o) => (
          <MenuItem key={o.id} value={o.id}>
            {o.name}
          </MenuItem>
        ))}
      </Select>
    );

    // Blok — obyekt tanlangan bo'lsa faqat o'sha obyekt bloklari
    const blockOptions = blocksList.filter(
      (b) => !filters.objectId || String(b.objects_id) === String(filters.objectId)
    );
    const blockSelect = (
      <Select
        size='small'
        value={filters.block ?? ''}
        displayEmpty
        onChange={(e) => updateFilter('block', e.target.value)}
      >
        <MenuItem value=''>{t('common.filter.all')}</MenuItem>
        {blockOptions.map((b) => (
          <MenuItem key={b.id} value={b.id}>
            {b.objects?.name ? `${b.objects.name} — ${b.name}` : b.name}
          </MenuItem>
        ))}
      </Select>
    );

    // Qavat — raqam (bo'sh = hammasi)
    const stageField = (
      <TextField
        size='small'
        type='number'
        inputProps={{ min: 0 }}
        placeholder={t('common.filter.all')}
        value={filters.stage ?? ''}
        onChange={(e) => updateFilter('stage', e.target.value)}
      />
    );

    const staffSelect = (
      <Select
        size='small'
        value={filters.staff ?? ''}
        displayEmpty
        onChange={(e) => updateFilter('staff', e.target.value)}
      >
        <MenuItem value=''>{t('common.filter.all')}</MenuItem>
        {staffsList.map((s) => (
          <MenuItem key={s.id} value={s.id}>
            {s.name}
          </MenuItem>
        ))}
      </Select>
    );

    const paymentTypeSelect = (
      <Select
        size='small'
        value={filters.type_id ?? ''}
        displayEmpty
        onChange={(e) => updateFilter('type_id', e.target.value)}
      >
        <MenuItem value=''>{t('common.filter.all')}</MenuItem>
        {paymentTypes.map((p) => (
          <MenuItem key={p.id} value={p.id}>
            {p.name}
          </MenuItem>
        ))}
      </Select>
    );

    const repairedSelect = (
      <Select
        size='small'
        value={filters.isrepired ?? ''}
        displayEmpty
        onChange={(e) => updateFilter('isrepired', e.target.value)}
      >
        <MenuItem value=''>{t('common.filter.all')}</MenuItem>
        <MenuItem value='1'>{t('excel.filters.repairedYes')}</MenuItem>
        <MenuItem value='0'>{t('excel.filters.repairedNo')}</MenuItem>
      </Select>
    );

    const fromDate = (
      <TextField
        size='small'
        type='date'
        value={filters.from ?? ''}
        onChange={(e) => updateFilter('from', e.target.value)}
        InputLabelProps={{ shrink: true }}
      />
    );

    const tillDate = (
      <TextField
        size='small'
        type='date'
        value={filters.till ?? ''}
        onChange={(e) => updateFilter('till', e.target.value)}
        InputLabelProps={{ shrink: true }}
      />
    );

    if (currentType === 'customs') {
      return (
        <>
          <FilterField className='!min-w-[240px] flex-1'>
            <label className='text-xs text-gray-500'>
              {t('excel.filters.search')}
            </label>
            <TextField
              size='small'
              placeholder={t('excel.filters.searchPlaceholder')}
              value={filters.search ?? ''}
              onChange={(e) => updateFilter('search', e.target.value)}
            />
          </FilterField>
          <FilterField>
            <label className='text-xs text-gray-500'>
              {t('excel.filters.clientType')}
            </label>
            <Select
              size='small'
              value={filters.client_type ?? ''}
              displayEmpty
              onChange={(e) => updateFilter('client_type', e.target.value)}
            >
              <MenuItem value=''>{t('common.filter.all')}</MenuItem>
              <MenuItem value='1'>{t('excel.filters.physical')}</MenuItem>
              <MenuItem value='2'>{t('excel.filters.legal')}</MenuItem>
            </Select>
          </FilterField>
        </>
      );
    }

    if (currentType === 'contracts') {
      return (
        <>
          <FilterField>
            <label className='text-xs text-gray-500'>
              {t('common.fields.fromDate')}
            </label>
            {fromDate}
          </FilterField>
          <FilterField>
            <label className='text-xs text-gray-500'>
              {t('common.fields.endDate')}
            </label>
            {tillDate}
          </FilterField>
          <FilterField>
            <label className='text-xs text-gray-500'>
              {t('common.fields.object')}
            </label>
            {objectSelect}
          </FilterField>
          <FilterField>
            <label className='text-xs text-gray-500'>
              {t('excel.filters.block')}
            </label>
            {blockSelect}
          </FilterField>
          <FilterField className='!min-w-[110px]'>
            <label className='text-xs text-gray-500'>
              {t('excel.filters.stage')}
            </label>
            {stageField}
          </FilterField>
          <FilterField>
            <label className='text-xs text-gray-500'>
              {t('common.fields.valute')}
            </label>
            {isvaluteOptions}
          </FilterField>
          <FilterField>
            <label className='text-xs text-gray-500'>
              {t('common.fields.status')}
            </label>
            <Select
              size='small'
              multiple
              value={Array.isArray(filters.status) ? filters.status : []}
              displayEmpty
              onChange={(e) => {
                const val = e.target.value;
                updateFilter('status', Array.isArray(val) ? val : []);
              }}
              renderValue={(selected) => {
                if (!selected?.length) return t('common.filter.all');
                const labels = {
                  0: t('excel.filters.statusSet'),
                  1: t('excel.filters.statusActive'),
                  2: t('excel.filters.statusComplete'),
                  3: t('excel.filters.statusCancel'),
                };
                return selected.map((s) => labels[s]).join(', ');
              }}
            >
              {[
                { v: '0', l: t('excel.filters.statusSet') },
                { v: '1', l: t('excel.filters.statusActive') },
                { v: '2', l: t('excel.filters.statusComplete') },
                { v: '3', l: t('excel.filters.statusCancel') },
              ].map((opt) => (
                <MenuItem key={opt.v} value={opt.v}>
                  <Checkbox
                    checked={
                      Array.isArray(filters.status) &&
                      filters.status.includes(opt.v)
                    }
                    size='small'
                  />
                  <ListItemText primary={opt.l} />
                </MenuItem>
              ))}
            </Select>
          </FilterField>
          <FilterField>
            <label className='text-xs text-gray-500'>
              {t('excel.filters.staff')}
            </label>
            {staffSelect}
          </FilterField>
          <FilterField>
            <label className='text-xs text-gray-500'>
              {t('excel.filters.repaired')}
            </label>
            {repairedSelect}
          </FilterField>
        </>
      );
    }

    if (currentType === 'debitors') {
      return (
        <>
          <FilterField>
            <label className='text-xs text-gray-500'>
              {t('common.fields.fromDate')}
            </label>
            {fromDate}
          </FilterField>
          <FilterField>
            <label className='text-xs text-gray-500'>
              {t('common.fields.endDate')}
            </label>
            {tillDate}
          </FilterField>
          <FilterField>
            <label className='text-xs text-gray-500'>
              {t('common.fields.object')}
            </label>
            {objectSelect}
          </FilterField>
          <FilterField>
            <label className='text-xs text-gray-500'>
              {t('excel.filters.block')}
            </label>
            {blockSelect}
          </FilterField>
          <FilterField className='!min-w-[110px]'>
            <label className='text-xs text-gray-500'>
              {t('excel.filters.stage')}
            </label>
            {stageField}
          </FilterField>
          <FilterField>
            <label className='text-xs text-gray-500'>
              {t('common.fields.valute')}
            </label>
            {isvaluteOptions}
          </FilterField>
          <FilterField>
            <label className='text-xs text-gray-500'>
              {t('excel.filters.staff')}
            </label>
            {staffSelect}
          </FilterField>
        </>
      );
    }

    if (currentType === 'payments') {
      return (
        <>
          <FilterField className='!min-w-[220px] flex-1'>
            <label className='text-xs text-gray-500'>
              {t('excel.filters.search')}
            </label>
            <TextField
              size='small'
              placeholder={t('excel.filters.searchPlaceholder')}
              value={filters.name ?? ''}
              onChange={(e) => updateFilter('name', e.target.value)}
            />
          </FilterField>
          <FilterField>
            <label className='text-xs text-gray-500'>
              {t('common.fields.fromDate')}
            </label>
            {fromDate}
          </FilterField>
          <FilterField>
            <label className='text-xs text-gray-500'>
              {t('common.fields.endDate')}
            </label>
            {tillDate}
          </FilterField>
          <FilterField>
            <label className='text-xs text-gray-500'>
              {t('common.fields.object')}
            </label>
            {objectSelect}
          </FilterField>
          <FilterField>
            <label className='text-xs text-gray-500'>
              {t('excel.filters.block')}
            </label>
            {blockSelect}
          </FilterField>
          <FilterField className='!min-w-[110px]'>
            <label className='text-xs text-gray-500'>
              {t('excel.filters.stage')}
            </label>
            {stageField}
          </FilterField>
          <FilterField>
            <label className='text-xs text-gray-500'>
              {t('common.fields.valute')}
            </label>
            {isvaluteOptions}
          </FilterField>
          <FilterField>
            <label className='text-xs text-gray-500'>
              {t('excel.filters.paymentType')}
            </label>
            {paymentTypeSelect}
          </FilterField>
          <FilterField>
            <label className='text-xs text-gray-500'>
              {t('excel.filters.staff')}
            </label>
            {staffSelect}
          </FilterField>
        </>
      );
    }

    if (currentType === 'income') {
      return (
        <>
          <FilterField>
            <label className='text-xs text-gray-500'>
              {t('common.fields.fromDate')}
            </label>
            {fromDate}
          </FilterField>
          <FilterField>
            <label className='text-xs text-gray-500'>
              {t('common.fields.endDate')}
            </label>
            {tillDate}
          </FilterField>
          <FilterField>
            <label className='text-xs text-gray-500'>
              {t('common.fields.object')}
            </label>
            {objectSelect}
          </FilterField>
          <FilterField>
            <label className='text-xs text-gray-500'>
              {t('excel.filters.block')}
            </label>
            {blockSelect}
          </FilterField>
          <FilterField className='!min-w-[110px]'>
            <label className='text-xs text-gray-500'>
              {t('excel.filters.stage')}
            </label>
            {stageField}
          </FilterField>
          <FilterField>
            <label className='text-xs text-gray-500'>
              {t('common.fields.valute')}
            </label>
            {isvaluteOptions}
          </FilterField>
          <FilterField>
            <label className='text-xs text-gray-500'>
              {t('excel.filters.staff')}
            </label>
            {staffSelect}
          </FilterField>
        </>
      );
    }

    return null;
  };

  return (
    <div className='p-4 flex flex-col gap-4'>
      {/* Tabs */}
      <div className='bg-white rounded-xl base-border shadow-sm px-2'>
        <Tabs
          value={activeTab}
          onChange={(_, val) => setActiveTab(val)}
          variant='scrollable'
          scrollButtons='auto'
        >
          {EXPORT_TYPES.map((type) => (
            <Tab
              key={type.key}
              label={
                <span className='flex items-center gap-2'>
                  <i className={type.icon} />
                  {t(type.label)}
                </span>
              }
            />
          ))}
        </Tabs>
      </div>

      {/* Filterlar paneli + Export tugmasi */}
      <div className='bg-white rounded-xl base-border shadow-sm p-4'>
        <div className='flex items-center gap-2 mb-3 pb-2 border-b'>
          <i className='bi bi-funnel text-blue-500' />
          <span className='font-semibold text-gray-700'>
            {t('excel.filters.title')}
          </span>
          {activeFilterCount > 0 && (
            <Chip
              size='small'
              label={`${t('excel.filters.active')}: ${activeFilterCount}`}
              onDelete={clearFilters}
              color='primary'
              variant='outlined'
              className='ml-1'
            />
          )}
          <div className='flex-1' />
          <Button
            variant='contained'
            color='success'
            size='medium'
            disabled={selectedColumns.length === 0 || exportLoading}
            onClick={handleExport}
            startIcon={
              exportLoading ? (
                <CircularProgress size={16} color='inherit' />
              ) : (
                <i className='bi bi-download' />
              )
            }
          >
            {t('excel.export')}
          </Button>
        </div>
        <div className='flex flex-wrap gap-3'>{renderFilters()}</div>
      </div>

      {/* Column constructor */}
      {columnsLoading ? (
        <div className='flex items-center justify-center h-40'>
          <CircularProgress size={30} color='inherit' />
        </div>
      ) : columns.length === 0 ? (
        <div className='flex items-center justify-center h-40 text-gray-400'>
          <i className='bi bi-exclamation-octagon text-xl mr-2' />
          {t('common.global.noDataFound')}
        </div>
      ) : (
        <div className='grid grid-cols-1 md:grid-cols-[1fr_auto_1fr] gap-4 items-start mb-4'>
          {/* CHAP: Mavjud */}
          <div className='bg-white rounded-xl p-4 base-border shadow-sm'>
            <div className='flex items-center justify-between mb-3 pb-2 border-b'>
              <div className='font-semibold text-gray-700 flex items-center gap-2'>
                <i className='bi bi-list-ul text-gray-500' />
                {t('excel.available')} ({availableColumns.length})
              </div>
              {availableColumns.length > 0 && (
                <Tooltip title={t('excel.selectAll')}>
                  <IconButton size='small' onClick={selectAll}>
                    <i className='bi bi-plus-square text-base text-green-600' />
                  </IconButton>
                </Tooltip>
              )}
            </div>
            {availableColumns.length === 0 ? (
              <div className='text-center text-gray-400 text-sm py-6'>
                {t('excel.allSelected')}
              </div>
            ) : (
              <ul className='flex flex-col gap-1 max-h-[440px] overflow-y-auto pr-1'>
                {availableColumns.map((col) => (
                  <li
                    key={col.code}
                    onClick={() => addColumn(col.code)}
                    className='group flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 hover:bg-blue-50 hover:border-blue-300 cursor-pointer transition-colors'
                  >
                    <span className='flex-1 text-sm text-gray-700 truncate'>
                      {col.label}
                    </span>
                    <i className='bi bi-arrow-right-circle text-gray-400 group-hover:text-blue-600 text-base' />
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* O'RTA */}
          <div className='hidden md:flex flex-col items-center justify-center gap-2 pt-12'>
            <Tooltip title={t('excel.selectAll')}>
              <span>
                <IconButton
                  size='small'
                  onClick={selectAll}
                  disabled={availableColumns.length === 0}
                  className='!border !border-gray-200 !bg-white'
                >
                  <i className='bi bi-chevron-double-right text-base' />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title={t('excel.clearAll')}>
              <span>
                <IconButton
                  size='small'
                  onClick={clearAll}
                  disabled={selectedColumns.length === 0}
                  className='!border !border-gray-200 !bg-white'
                >
                  <i className='bi bi-chevron-double-left text-base' />
                </IconButton>
              </span>
            </Tooltip>
          </div>

          {/* O'NG: Tanlangan */}
          <div className='bg-white rounded-xl p-4 base-border shadow-sm'>
            <div className='flex items-center justify-between mb-3 pb-2 border-b'>
              <div className='font-semibold text-gray-700 flex items-center gap-2'>
                <i className='bi bi-check2-square text-green-600' />
                {t('excel.selected')} ({selectedColumns.length})
              </div>
              <div className='flex gap-1'>
                <Tooltip title={t('excel.resetOrder')}>
                  <IconButton size='small' onClick={resetOrder}>
                    <i className='bi bi-arrow-counterclockwise text-base' />
                  </IconButton>
                </Tooltip>
                <Tooltip title={t('excel.clearAll')}>
                  <span>
                    <IconButton
                      size='small'
                      onClick={clearAll}
                      disabled={selectedColumns.length === 0}
                    >
                      <i className='bi bi-x-circle text-base text-red-500' />
                    </IconButton>
                  </span>
                </Tooltip>
              </div>
            </div>
            {selectedColumns.length === 0 ? (
              <div className='text-center text-gray-400 text-sm py-6'>
                {t('excel.noSelected')}
              </div>
            ) : (
              <ul className='flex flex-col gap-1 max-h-[440px] overflow-y-auto pr-1'>
                {selectedColumns.map((code, idx) => (
                  <li
                    key={code}
                    className='flex items-center gap-1 bg-green-50 border border-green-200 rounded-lg px-2 py-1.5'
                  >
                    <span className='inline-flex items-center justify-center w-6 h-6 rounded-md bg-green-600 text-white text-xs font-bold shrink-0'>
                      {idx + 1}
                    </span>
                    <span className='flex-1 text-sm text-gray-800 truncate ml-1'>
                      {labelByCode[code] || code}
                    </span>
                    <Tooltip title={t('excel.moveUp')}>
                      <span>
                        <IconButton
                          size='small'
                          disabled={idx === 0}
                          onClick={() => move(idx, -1)}
                          className='!p-0.5'
                        >
                          <i className='bi bi-chevron-up text-xs' />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title={t('excel.moveDown')}>
                      <span>
                        <IconButton
                          size='small'
                          disabled={idx === selectedColumns.length - 1}
                          onClick={() => move(idx, 1)}
                          className='!p-0.5'
                        >
                          <i className='bi bi-chevron-down text-xs' />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title={t('excel.remove')}>
                      <IconButton
                        size='small'
                        onClick={() => removeColumn(code)}
                        className='!p-0.5'
                      >
                        <i className='bi bi-x text-base text-red-500' />
                      </IconButton>
                    </Tooltip>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}

      {/* Tarix */}
      <div className='bg-white rounded-xl p-4 base-border shadow-sm'>
        <div className='flex items-center justify-between mb-3 pb-2 border-b'>
          <div className='font-semibold text-gray-700 flex items-center gap-2'>
            <i className='bi bi-clock-history text-blue-500' />
            {t('excel.history')} ({history.total})
          </div>
          <Tooltip title={t('common.button.refresh')}>
            <IconButton
              size='small'
              onClick={() => fetchHistory(historyPage)}
              disabled={historyLoading}
            >
              <i
                className={`bi bi-arrow-repeat${
                  historyLoading ? ' animate-spin' : ''
                }`}
              />
            </IconButton>
          </Tooltip>
        </div>

        {historyLoading && history.data.length === 0 ? (
          <div className='flex justify-center py-6'>
            <CircularProgress size={24} />
          </div>
        ) : history.data.length === 0 ? (
          <div className='text-center text-gray-400 text-sm py-6'>
            {t('excel.noExports')}
          </div>
        ) : (
          <>
            <div className='overflow-x-auto'>
              <table className='w-full text-sm'>
                <thead>
                  <tr className='border-b text-left text-gray-500 text-xs'>
                    <th className='py-2 px-2 w-12'>#</th>
                    <th className='py-2 px-2'>{t('excel.fileName')}</th>
                    <th className='py-2 px-2 whitespace-nowrap'>
                      {t('excel.dateTime')}
                    </th>
                    <th className='py-2 px-2 w-32 text-right'>
                      {t('common.table.actions')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {history.data.map((item, idx) => (
                    <tr
                      key={item.id}
                      className='border-b hover:bg-gray-50 transition'
                    >
                      <td className='py-2 px-2 text-gray-400'>
                        {(historyPage - 1) * 15 + idx + 1}
                      </td>
                      <td className='py-2 px-2 text-gray-800'>
                        <div className='flex items-center gap-2'>
                          <i className='bi bi-file-earmark-excel text-green-600' />
                          <span className='truncate'>{item.filename}</span>
                        </div>
                      </td>
                      <td className='py-2 px-2 text-gray-600 whitespace-nowrap'>
                        {item.date_time
                          ? moment(item.date_time).format('DD.MM.YYYY HH:mm')
                          : '—'}
                      </td>
                      <td className='py-2 px-2 text-right'>
                        <Tooltip title={t('excel.download')}>
                          <IconButton
                            size='small'
                            onClick={() =>
                              downloadFromLink(item.link, item.filename)
                            }
                          >
                            <i className='bi bi-download text-blue-600 text-base' />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={t('common.button.delete')}>
                          <IconButton
                            size='small'
                            onClick={() => handleDeleteExport(item.id)}
                          >
                            <i className='bi bi-trash text-red-500 text-base' />
                          </IconButton>
                        </Tooltip>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {history.lastPage > 1 && (
              <div className='flex justify-center mt-3'>
                <Pagination
                  count={history.lastPage}
                  page={historyPage}
                  onChange={(_, p) => setHistoryPage(p)}
                  size='small'
                  color='primary'
                />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
